const OTA_APPLYING_READY_KEY = "template-ota-applying-ready-pending";
const OTA_APPLYING_READY_TTL_MS = 2 * 60 * 1000;

export const OTA_APPLYING_READY_EVENT = "template:ota-applying-ready";

interface OtaApplyingReadyState {
  version: string | null;
  startedAt: number;
}

function emitApplyingReadyChange(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OTA_APPLYING_READY_EVENT));
}

function readApplyingReadyState(): OtaApplyingReadyState | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(OTA_APPLYING_READY_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<OtaApplyingReadyState>;
    if (typeof parsed.startedAt !== "number") {
      window.localStorage.removeItem(OTA_APPLYING_READY_KEY);
      return null;
    }

    if (Date.now() - parsed.startedAt > OTA_APPLYING_READY_TTL_MS) {
      window.localStorage.removeItem(OTA_APPLYING_READY_KEY);
      return null;
    }

    return {
      version: typeof parsed.version === "string" ? parsed.version : null,
      startedAt: parsed.startedAt,
    };
  } catch {
    return null;
  }
}

export function markOtaApplyingReadyPending(version: string | null | undefined): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      OTA_APPLYING_READY_KEY,
      JSON.stringify({ version: version ?? null, startedAt: Date.now() }),
    );
    emitApplyingReadyChange();
  } catch {
    /* noop */
  }
}

export function clearOtaApplyingReadyPending(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(OTA_APPLYING_READY_KEY);
    emitApplyingReadyChange();
  } catch {
    /* noop */
  }
}

export function isOtaApplyingReadyPending(): boolean {
  return readApplyingReadyState() !== null;
}

export function subscribeOtaApplyingReadyPending(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  window.addEventListener(OTA_APPLYING_READY_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(OTA_APPLYING_READY_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}
